import { Alert, Button, Modal, Spinner, Typography } from "@heroui/react";
import { Icon } from "@iconify/react";

import { extractErrorMessage } from "@/utils/error";

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  isPending: boolean;
  error?: Error | null;
  /** The confirm button, e.g. "Revoke" or "Leave"; "Delete" by default. */
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}

export const ConfirmDeleteModal = ({
  isOpen,
  title,
  description,
  isPending,
  error,
  confirmLabel = "Delete",
  onConfirm,
  onClose,
}: ConfirmDeleteModalProps) => {
  const errorMessage = error ? extractErrorMessage(error) : null;
  const errorTitle = `Couldn't ${confirmLabel.toLowerCase()}`;

  return (
    <Modal>
      <Modal.Backdrop
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <Modal.Container size="sm">
          <Modal.Dialog>
            <Modal.Header className="flex items-center gap-2">
              <Icon
                className="text-danger"
                fontSize={20}
                icon="solar:trash-bin-trash-linear"
              />
              <Modal.Heading>{title}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-3">
              {errorMessage ? (
                <Alert status="danger">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>{errorTitle}</Alert.Title>
                    <Alert.Description>{errorMessage}</Alert.Description>
                  </Alert.Content>
                </Alert>
              ) : null}
              <Typography type="body-sm">{description}</Typography>
            </Modal.Body>
            <Modal.Footer>
              <Button
                isDisabled={isPending}
                variant="tertiary"
                onPress={onClose}
              >
                Cancel
              </Button>
              <Button
                isPending={isPending}
                variant="danger"
                onPress={onConfirm}
              >
                {({ isPending: pending }) => (
                  <>
                    {pending && <Spinner color="current" size="sm" />}
                    {confirmLabel}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
};
